import { ARTIFACT_TYPES, type ArtifactType } from '@/types/artifacts';
import {
  AUDIO_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
  DOCUMENT_EXTENSIONS_EXTRA,
  IMAGE_EXTENSIONS,
  PRESENTATION_EXTENSIONS,
  PRESENTATION_EXTENSIONS_EXTRA,
  SPREADSHEET_EXTENSIONS,
  TEXT_EXTENSIONS,
  VIDEO_EXTENSIONS,
} from '@/types/source-types';
import { getFileTypeLabel } from '@/utils/get-file-type-label';

const LABELS: Record<ArtifactType, string> = {
  web_page: 'Web Page',
  document: 'Document',
  spreadsheet: 'Spreadsheet',
  presentation: 'Presentation',
  data_viz: 'Data Viz',
  image: 'Image',
  audio: 'Audio',
  video: 'Video',
  other: 'File',
};

export function artifactTypeLabel(type: ArtifactType): string {
  return LABELS[type] ?? 'File';
}

export function isArtifactType(
  value: string | undefined,
): value is ArtifactType {
  return !!value && (ARTIFACT_TYPES as readonly string[]).includes(value);
}

function typesFor(
  extensions: readonly string[],
  type: ArtifactType,
): Record<string, ArtifactType> {
  return Object.fromEntries(extensions.map((extension) => [extension, type]));
}

// Built from the shared extension lists, so the type a card names and the icon
// it draws can never disagree. The literals below are the families those lists
// have no reason to carry: they exist for upload pickers and file icons, not
// for the Artifact taxonomy.
const TYPE_BY_EXTENSION: Record<string, ArtifactType> = {
  ...typesFor(SPREADSHEET_EXTENSIONS, 'spreadsheet'),
  ...typesFor(PRESENTATION_EXTENSIONS, 'presentation'),
  ...typesFor(PRESENTATION_EXTENSIONS_EXTRA, 'presentation'),
  ...typesFor(DOCUMENT_EXTENSIONS, 'document'),
  ...typesFor(DOCUMENT_EXTENSIONS_EXTRA, 'document'),
  ...typesFor(TEXT_EXTENSIONS, 'document'),
  ...typesFor(IMAGE_EXTENSIONS, 'image'),
  ...typesFor(VIDEO_EXTENSIONS, 'video'),
  ...typesFor(AUDIO_EXTENSIONS, 'audio'),
  pdf: 'document',
  json: 'document',
  html: 'web_page',
  htm: 'web_page',
  dot: 'data_viz',
  gv: 'data_viz',
  // The backend leaves columnar data unclassified; don't disagree with it.
  parquet: 'other',
};

// An extension starts with a letter, so a trailing version fragment such as
// "Report v1.2" is not mistaken for one.
const FILE_EXTENSION = /\.([a-z][a-z0-9]{1,7})$/i;

export function looksLikeFileName(value: string | undefined): value is string {
  return !!value && FILE_EXTENSION.test(value);
}

function typeFromMime(mimeType: string | undefined): ArtifactType | undefined {
  const mime = (mimeType ?? '').split(';')[0].trim().toLowerCase();
  if (!mime) return undefined;
  // Codec-specific subtypes are endless, so trust the family prefix.
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  // getFileTypeLabel already maps the MIME types the API returns onto an
  // extension, so classify through it rather than restating the table.
  return TYPE_BY_EXTENSION[getFileTypeLabel(mime)];
}

/**
 * Classify a file by its name, falling back to its MIME type. Returns
 * `undefined` when neither says anything useful, so callers keep whatever type
 * they already have.
 */
export function artifactTypeFromFile(
  fileName: string | undefined,
  mimeType?: string,
): ArtifactType | undefined {
  const extension = fileName?.match(FILE_EXTENSION)?.[1]?.toLowerCase();
  return (
    (extension ? TYPE_BY_EXTENSION[extension] : undefined) ??
    typeFromMime(mimeType)
  );
}
